import { WORKER_CONSUMER_CONFIG } from "./event-subjects.js";
import { z } from "zod";
//#region src/work.ts
/**
* How ONE attempt runs. ⛔ This is the only axis on which the four legacy mechanisms differed that
* an app may choose, and it is a CLOSED set of three (OVERVIEW §3.1).
*
* - `inline`   — runs in the standing subscriber process.        (was: M1 worker)
* - `unit`     — one ephemeral pod per attempt, credentialed.    (was: M2 compute unit)
* - `isolated` — one sealed pod per attempt, zero credential.    (was: M3 pool)
*
* ⛔ THERE IS NO FOURTH MEMBER FOR CRON. *Cron is a TRIGGER, not an executor* (OVERVIEW §2) —
* that is precisely how M4 collapses in without a fourth execution path. A `schedule` arm here
* would re-create the thing the model exists to remove.
*
* ⛔ AND THERE IS NO `authenticated` FLAG ANYWHERE NEAR THIS TYPE. Whether the payload is
* authenticated is a CONSEQUENCE of the shape, never a settable one (OVERVIEW §5.1): `inline`
* shares a process with the trusted receiver, `unit` and `isolated` do not, and a credential
* inside an isolated pod is readable by the code it runs. Exposing it as an option would let an
* app select "sealed but trusted", which is incoherent.
*/
const EXECUTION_SHAPES = [
	"inline",
	"unit",
	"isolated"
];
/** Zod enum over {@link EXECUTION_SHAPES}. */
const executionShapeSchema = z.enum(EXECUTION_SHAPES);
/**
* True iff `v` is an {@link ExecutionShape}.
*
* ⚠️ INTENDED as the sole narrowing, and **NOT YET ADOPTED** — measured 2026-08-23, zero
* narrowing sites in production; the Stage 2 canary is the first. Stated because the previous
* wording ("the only sanctioned narrowing") told a reader the discipline was ENFORCED, which
* would let the next person widen a `switch` elsewhere and never learn this exists. A docblock
* that describes an intention as a fact is worse than no docblock.
*/
function isExecutionShape(v) {
	return typeof v === "string" && EXECUTION_SHAPES.includes(v);
}
/**
* ⛔⛔ **`runId` AND `attemptId` ARE DIFFERENT TYPES. DO NOT CONFLATE THEM.** (Lead ruling,
* 2026-08-22. This amends OVERVIEW §3.3, which is silent on the distinction.)
*
* **One run has N attempts under at-least-once redelivery, and therefore N pods.** So a `runId`
* that is a pod name is not merely untidy — it is **structurally unrepresentable**.
*
* ⛔ TODAY THE TWO ARE THE SAME VALUE, AND **WS-2's CG-4 UNWINDS IT — NOT WS-7.** (Confirmed with
* WS-2, 2026-08-22: their CG-4 re-keys `abortControllers` from `jobId` to `runId` and threads the
* real `runId` in from admission, leaving `generateJobId` unchanged as `attemptId`'s minter.
* Two workstreams must not both edit the admission path of the pool runner's `pool-runner.ts`;
* WS-4 §13.2 records the same ordering constraint: WS-2's CG-4 lands before WS-4's CG-3.)
* The pool runner returned `{ runId: job.jobId, … }`, where `jobId` was constrained to 32 lowercase
* hex by `ISOLATED_POOL_JOB_ID_PATTERN` in `isolated-pool-naming.ts`, because it became the pod name
* `ipj-<jobId>`, a 36-char DNS-1123 object name. The pool runner, `pool-runner.ts` and
* `isolated-pool-naming.ts` left the operator in stage S3 of 2026-10-14-app-pools-runtime-deletion.
* The attempt id keeps the 32-hex shape; see {@link WORK_ATTEMPT_ID_HEX_LEN}.
*
* - {@link workRunIdSchema}     — one per SUBMISSION. Durable, returned at admission, resolvable
*                                 from any process, survives the caller's restart. NOT a pod name.
* - {@link workAttemptIdSchema} — one per POD. Keeps today's 32-hex grammar UNTOUCHED.
*/
/**
* ⭐⭐ **A `runId` IS AN IDENTIFIER, NEVER A CREDENTIAL — AND THIS SENTENCE IS THE POINT OF THE
* DOCBLOCK, NOT AN ASIDE.**
*
* It is time-ordered and therefore **partly predictable**, deliberately (see the grammar below).
* That is safe **because nothing authorises on it**: every door guarding `get`, `stream` and
* `cancel` authorises on the **CALLER'S IDENTITY**, and possession of a `runId` is evidence of
* nothing. ⛔ If any consumer ever treats knowing a `runId` as permission, that is a
* vulnerability, not a shortcut — and the correct fix is at that door, never a change to this
* grammar.
*
* ⚠️ THE MEASURED REASON THIS PARAGRAPH EXISTS. The one production caller of the compute-unit
* broker was handed an idempotency key (`ownerRef`) and **silently destroyed it** by embedding a
* freshly-minted `randomBytes(32)` bearer nonce, so its dedupe branch has never once fired — and
* **nothing went red**, because the field was still present and still typed.
* ⭐ *Stable-to-dedupe and unguessable-to-authenticate are in direct opposition. One string cannot
* satisfy both, so whichever property the author had in mind that day is the one that survives.*
* ⇒ The docblock is the control. It is what stops the next author re-deriving that mistake by
* "hardening" this id into a secret and destroying its ordering — or by leaning on its
* predictability as if it were a secret.
*
* ## The grammar
*
* A lowercase **ULID**: 26 characters of Crockford base32, whose first 10 characters are the
* admission timestamp in milliseconds and are **lexicographically sortable**.
*
* ⛔⛔ **OPAQUE MEANING, NORMATIVE SHAPE. THE CONTRACT PUBLISHES THE PATTERN AND SAYS NOTHING
* ABOUT WHAT THE BYTES MEAN.** Agreed with WS-2 (ESC-5, closed), and it is consistency with a
* docblock this package ALREADY SHIPS on `WorkPoolRunResult.runId` in `work-pool.ts`: *"Opaque
* platform correlation id. ⛔ NOT a pod name — I-1 keeps Kubernetes off this surface"*. sdk-node's
* `work-pool/work-pool.ts` restated it until that module left. Documenting a decodable timestamp
* here would put this contract in direct conflict with that statement.
*
* ⇒ The SHAPE is published, because it is load-bearing for door validation, for the run store's
* column and for its `CHECK`. The SEMANTICS are not, for two reasons: if the contract says *"the
* first 10 characters are a timestamp"* a client will decode them, which (a) freezes the encoding
* forever and (b) publishes a **second, weaker source of truth for "when was this admitted"** that
* can disagree with {@link RunStatus.startedAt}. And a client-side expiry computation is
* **structurally unable to be correct**, because only the server knows the retention horizon.
*
* ⚠️ The ordering IS real and IS used — decoded in exactly one place, the operator's `read()`,
* which is **the same component that minted it**. That is a private encoding, not a layering
* violation. ⭐ WS-2's `T-26` is a fuse asserting the SDK client contains **no** decode of a
* `runId` — no slice, no `parseInt`, no base32 call — because *a published adjective that nothing
* checks is how "opaque" rots*.
*
* ⛔⛔ **CANONICAL FORM ONLY. NEVER NORMALISE, CASE-FOLD OR LENIENTLY MATCH — THIS IS THE ONE THAT
* BITES IF IT IS NOT WRITTEN DOWN.** Crockford base32 is **deliberately lenient on decode by
* spec**: `I`/`L` decode as `1`, `O` decodes as `0`, and it is case-insensitive. The run store
* matches `run_id` as a **`text` PRIMARY KEY, byte-exactly, in SQL.** ⇒ if any component reaches
* for a real Crockford decoder, `0jd…` and `Ojd…` are **the same id to the decoder and different
* rows to the database** — two ids simultaneously equal and not equal, surfacing as a phantom
* `'unknown-run'`. ⇒ **A non-canonical id is `'invalid-args'` at the door. It is never repaired.**
* `W-I4` pins one row per confusable (`i`, `l`, `o`, `u`, and an uppercase variant).
*
* ⚠️ **THERE IS NO ULID LIBRARY IN THIS TREE** — measured by WS-2: `ulid` / `ulidx` /
* `monotonicFactory` = **0** hits across `cloud` and `tools`, against a live control of **6**
* `package.json` files declaring `nats`. The ~30 lines of encode/decode are **WS-2's**, in
* `zion-operator/src/work-store/run-id.ts`. **WS-1 owns only the type, the pattern and this
* docblock.** Adding a dependency would be a supply-chain call for the lead, not a drive-by.
*/
const WORK_RUN_ID_LENGTH = 26;
/**
* {@link WORK_RUN_ID_LENGTH} characters of lowercase Crockford base32 — the 32-symbol alphabet
* that excludes `i`, `l`, `o` and `u`.
*
* ⛔ **THE CLASS IS ENUMERATED, NOT A RANGE, AND THAT IS WS-2'S CONDITION 3 — A COLLATION
* ARGUMENT, NOT A STYLE ONE.** The identical string is also a Postgres `CHECK` constraint on the
* run store's `run_id` column, and **Postgres regex character RANGES are collation-dependent**:
* `[a-h]` is not guaranteed to mean those eight ASCII letters under a non-C collation. Writing the
* class out removes the question instead of answering it per-deployment. ⚠️ VERIFIED EQUIVALENT to
* the range form `[0-9a-hjkmnp-tv-z]` by execution over the printable range — both yield **exactly
* 32 symbols**, symmetric difference empty — so this is a pure notation change and not a widening.
* ⛔ There are THREE copies of this grammar (here, WS-2's SQL `CHECK`, WS-2's `RUN_ID_PATTERN`);
* making all three byte-identical is the cheapest thing that stops them drifting.
*
* ⛔ DERIVED from {@link WORK_RUN_ID_LENGTH}, never a second literal.
*
* ⛔⛔ **THIS IS THE ONE DECLARATION. EVERY OTHER TYPESCRIPT SITE IMPORTS IT — INCLUDING THE
* OPERATOR'S `RUN_ID_PATTERN`, WHICH BECOMES A RE-EXPORT AND NOT A SECOND DECLARATION.**
* (Lead ruling, 2026-08-22.) WS-2 §17 row 10 requires the pattern be byte-identical across its
* copies; **a re-export discharges that BY CONSTRUCTION rather than by assertion**, and the
* "three copies" problem stops existing rather than being tested.
* ⛔ Do NOT "solve" a divergence here with a `.source`-equality test between two declarations.
* Two *set-equal but not byte-equal* forms — the range `[0-9a-hjkmnp-tv-z]` and the enumerated
* `[0-9abcdefghjkmnpqrstvwxyz]` — make such a test **red on a pure notation difference while the
* grammars agree**: a gate that fails for the wrong reason, which trains people to wave it away,
* which is how the next real divergence gets waved away too.
*
* ⚠️ **THE SQL `CHECK` IS THE ONE SITE THAT LEGITIMATELY CANNOT IMPORT THIS, AND IT MUST STAY
* ENUMERATED.** Postgres regex character RANGES are collation-dependent, so the constraint spells
* the class out. That pair — this constant and the `CHECK` — genuinely IS two copies, so it gets a
* real check: `W-I5` asserts the two accept **the same set**, character by character over the
* printable ASCII range, rather than comparing their text. ⇒ **One structural guarantee for the
* TypeScript sites, one behavioural test for the one site that cannot participate.**
*/
const WORK_RUN_ID_PATTERN = new RegExp(`^[0-9abcdefghjkmnpqrstvwxyz]{26}$`);
/** A submission's durable id. See {@link WORK_RUN_ID_LENGTH} for the identifier-not-credential rule. */
const workRunIdSchema = z.string().regex(WORK_RUN_ID_PATTERN);
/**
* The number of hex characters in an {@link workAttemptIdSchema}.
*
* ⛔ **32, AND IT IS NOT THIS CONTRACT'S NUMBER TO CHOOSE.** It is
* `ISOLATED_POOL_JOB_ID_HEX_LEN` in the operator's `isolated-pool/isolated-pool-naming.ts`, which
* left the operator in stage S3 of 2026-10-14-app-pools-runtime-deletion. That file recorded the
* reason: `ipj-` + 32 hex = 36 chars, inside DNS-1123's 63 and inside every
* label-value ceiling, with room for a `-np` / `-svc` suffix. Widening it here would let the CP
* mint an attempt id the operator cannot turn into a pod name.
*
* ⛔ **IT IS RESTATED HERE RATHER THAN IMPORTED, AND THAT IS FORCED, NOT LAZY.**
* `@zion/sdk-contracts` is the LEAF package; importing from `@zion/zion-operator` is the reverse
* edge and the `contracts` rule of `RULES` in `dependency-direction.test.ts` forbids it. ⇒ ⚠️ **SEAM OBLIGATION ON WS-4/WS-7,
* and it must be discharged in the OPERATOR's suite because only that side can see both symbols:**
* replace `ISOLATED_POOL_JOB_ID_HEX_LEN` / `ISOLATED_POOL_JOB_ID_PATTERN` with re-exports of these,
* and until then pin them equal by test THERE. `W-I3` asserts the property from this side to the
* extent this side can see it (the pattern is derived from the constant, not typed twice).
*/
const WORK_ATTEMPT_ID_HEX_LEN = 32;
/** {@link WORK_ATTEMPT_ID_HEX_LEN} lowercase hex characters. ⛔ DERIVED from the constant, never a
*  second literal — the X-22 two-bound shape this package has already been bitten by. */
const WORK_ATTEMPT_ID_PATTERN = new RegExp(`^[0-9a-f]{32}$`);
/**
* One ATTEMPT's id — one per pod. `ipj-<attemptId>` is the pod's object name.
*
* ⛔ NOT interchangeable with a {@link workRunIdSchema}. A caller holding one of these holds a
* reference to one *try*, not to the work; the run outlives it.
*/
const workAttemptIdSchema = z.string().regex(WORK_ATTEMPT_ID_PATTERN);
/**
* SHAPE bound on an idempotency key, in characters.
*
* ⛔⛔ **AN IDEMPOTENCY KEY IS A DISTINCT FIELD THAT CARRIES NOTHING ELSE, AND IT IS NOT A
* CREDENTIAL.** (Lead ruling, 2026-08-22 — the contract text must say so, and this is it.)
* OVERVIEW §5.2: the only production caller was handed one (`ownerRef`) and **silently defeated
* it** by embedding a freshly-minted `randomBytes(32)` bearer nonce, so the dedupe branch has never
* once fired — and **nothing went red**, because the type still showed a key and the call still
* succeeded. *Stable-to-dedupe and unguessable-to-authenticate are in direct opposition; one string
* cannot satisfy both.* This key is its own field, carries nothing else, and is logged.
*
* ⚠️ **255 AND NOT 128 — I MEASURED RATHER THAN PREFERRED.** This plan's own standing rule is
* *start tight; narrowing later is impossible and widening is always available*, so a departure
* needs a number. A realistic composite key of the form `<uuid>:<uuid>:<sha256hex>` is
* **36 + 1 + 36 + 1 + 64 = 138 characters**, so 128 is measurably too tight — it would force
* callers to **hash** their key, destroying the human-readability that is the operational value of
* a stable key and pushing them toward exactly the "mint something opaque" instinct that produced
* the `randomBytes(32)` defect above. 255 also matches the run store's column width, so the two
* cannot disagree.
*/
const WORK_IDEMPOTENCY_KEY_MAX_LENGTH = 255;
/**
* The idempotency-key charset: printable, delimiter-free, safe in a log line and in a database
* unique index. ⛔ Deliberately EXCLUDES whitespace and every character `safeRuleLabel`
* (`./fixture-placement-rules.ts`) would have to scrub, so the value can be echoed in an
* error without a sanitiser — the `js/log-injection` class this repo has already been bitten by.
*/
const WORK_IDEMPOTENCY_KEY_PATTERN = new RegExp(`^[A-Za-z0-9._:-]{1,255}$`);
/** {@link WORK_IDEMPOTENCY_KEY_PATTERN}, as a schema. */
const workIdempotencyKeySchema = z.string().min(1).max(255).regex(WORK_IDEMPOTENCY_KEY_PATTERN);
/**
* SHAPE bound on a caller-supplied `timeoutMs` (1 hour). ⛔ The SEMANTIC bound is the TARGET's
* declared `maxDurationSeconds`, enforced by the executor — the same split
* `MAX_WORK_POOL_TIMEOUT_MS` already uses (`./work-pool.ts`). A caller may ask for LESS;
* asking for MORE is `reason: 'invalid-args'`, never a silent clamp (OVERVIEW §3.3).
*
* Numerically equal to `MAX_WORK_POOL_TIMEOUT_MS`, and `W-B3` pins the two equal rather than
* importing, because the pool constant is scheduled for retirement by WS-7 and an import would
* make this surface's bound disappear with it.
*/
const MAX_WORK_TIMEOUT_MS = 72e5;
/**
* The machine-readable failure vocabulary for the unified work API.
*
* ⛔ THIS IS A NEW, CLOSED UNION — NOT an alias of `WORK_POOL_REASONS` (`./work-pool.ts`). Three members are
* RENAMED away from Kubernetes/pool nouns (`unknown-pool`→`unknown-target`,
* `args-too-large`→`payload-too-large`, `runner-unavailable`→`executor-unavailable`,
* `job-failed`→`attempt-failed`, `runner-not-configured`→`not-configured`) because the unified
* surface has three shapes and "runner" names exactly one of them. One member is NEW
* (`unknown-run`). Renaming is free TODAY and impossible after the first adopter — OVERVIEW §3.5.
*
* ⛔ THE TWO UNIONS COEXIST UNTIL WS-7 RETIRES THE POOL SURFACE, so the mapping between them is
* declared here, beside them, as a total {@link Record} — see {@link WORK_REASON_FROM_WORK_POOL_REASON}.
*
* ⛔ THE SPLIT BETWEEN SERVER- AND CLIENT-ORIGINATED IS A PREDICATE, NEVER A COUNT. `work-pool.ts`
* carried *"nine of the eleven"* and **both numbers were wrong** because the union grew twice in one
* night. Read the per-member annotations; do not write a total.
*/
const WORK_REASONS = [
	"unknown-target",
	"unknown-run",
	"expired-run",
	"compute-refused",
	"target-retired",
	"not-authorized",
	"app-removed",
	"invalid-args",
	"payload-too-large",
	"timeout",
	"capacity",
	"capacity-ceiling",
	"pod-start-failed",
	"node-provisioning-timeout",
	"node-capacity-exhausted",
	"image-pull-timeout",
	"image-pull-failed",
	"substrate-userns-unsupported",
	"attempt-failed",
	"preempted",
	"result-too-large",
	"executor-unavailable",
	"queue-unavailable",
	"containment-unverified",
	"malformed-response",
	"not-configured"
];
const WORK_REASON_VALUES = new Set(WORK_REASONS);
/** Type guard — the ONLY sanctioned way to narrow a server-supplied reason string. A reason this
*  SDK version does not recognise must degrade to `null`, never throw. */
function isWorkReason(value) {
	return typeof value === "string" && WORK_REASON_VALUES.has(value);
}
/**
* The current reason-vocabulary contract version. Monotonic, starts at `1`.
*
* ⛔ THE RULE THIS NUMBER ENFORCES (OVERVIEW §6 H-forwardcompat): *"A new transient reason must ship
* to the union BEFORE any server emits it."* `.strict()` + a closed union + fail-closed-on-unknown
* means an older client treats a NEW TRANSIENT reason as PERMANENT — a job that should have retried
* simply stops. The mechanism below makes the one-release gap structural instead of a convention.
*
* ⛔ ADDING A MEMBER TO {@link WORK_REASONS} IS A THREE-STEP, TWO-RELEASE CHANGE:
*   RELEASE N   — (1) add the member; (2) add its {@link WorkReasonSpec} with `introducedInVersion`
*                 set to the CURRENT value of this constant, written as a NUMBER LITERAL (today
*                 `2`). ⛔ NEVER write the constant itself there: a spec that names the constant
*                 moves with every bump, so `isEmittableAtVersion(v, v)` stays `false` and the
*                 member is dormant for good; (3) update the frozen list in
*                 `work-reasons-contract-version.test.ts`.
*                 ⛔ DO NOT bump this constant in release N. The member is now KNOWN to clients
*                 and {@link isEmittableWorkReason} answers `false`, so no server can emit it.
*   RELEASE N+1 — bump this constant by one, and update `EXPECTED_VERSION` in that same test.
*                 The member's literal now sits below the constant, so it becomes emittable.
*                 `W-F4` reds on the bump and names the members that woke; retire them from its
*                 dormant list in the same edit.
*
* ⚠️ THE HONEST LIMIT, STATED RATHER THAN IMPLIED: this enforces that a RELEASE BOUNDARY separates
* "the member exists" from "a server emits it". It does NOT prove every client has upgraded — no
* in-repo artifact can, because app pods pin their own `@zion/sdk-node` version. What it removes is
* the same-release race, which is the failure that is actually reachable by accident.
*
* Version 2 since 2026-10-24-pool-machine-generations (D-30, release N+1). The bump woke the six
* members whose specs carry the literal `1`: `app-removed`, `node-provisioning-timeout`,
* `node-capacity-exhausted`, `image-pull-timeout`, `image-pull-failed` and `preempted`.
*/
const WORK_REASONS_CONTRACT_VERSION = 2;
/** ⛔ EXHAUSTIVE BY TYPE. See {@link WorkReasonSpec}. */
const WORK_REASON_SPECS = {
	"unknown-target": {
		retryable: false,
		introducedInVersion: 0
	},
	"unknown-run": {
		retryable: false,
		introducedInVersion: 0
	},
	"expired-run": {
		retryable: false,
		introducedInVersion: 0
	},
	"compute-refused": {
		retryable: false,
		introducedInVersion: 0
	},
	"target-retired": {
		retryable: false,
		introducedInVersion: 0
	},
	"not-authorized": {
		retryable: false,
		introducedInVersion: 0
	},
	"app-removed": {
		retryable: false,
		introducedInVersion: 1
	},
	"invalid-args": {
		retryable: false,
		introducedInVersion: 0
	},
	"payload-too-large": {
		retryable: false,
		introducedInVersion: 0
	},
	"timeout": {
		retryable: true,
		introducedInVersion: 0
	},
	"capacity": {
		retryable: true,
		introducedInVersion: 0
	},
	"capacity-ceiling": {
		retryable: false,
		introducedInVersion: 0
	},
	"pod-start-failed": {
		retryable: true,
		introducedInVersion: 0
	},
	"node-provisioning-timeout": {
		retryable: true,
		introducedInVersion: 1
	},
	"node-capacity-exhausted": {
		retryable: true,
		introducedInVersion: 1
	},
	"image-pull-timeout": {
		retryable: true,
		introducedInVersion: 1
	},
	"image-pull-failed": {
		retryable: true,
		introducedInVersion: 1
	},
	"substrate-userns-unsupported": {
		retryable: false,
		introducedInVersion: 0
	},
	"attempt-failed": {
		retryable: false,
		introducedInVersion: 0
	},
	"preempted": {
		retryable: true,
		introducedInVersion: 1
	},
	"result-too-large": {
		retryable: false,
		introducedInVersion: 0
	},
	"executor-unavailable": {
		retryable: true,
		introducedInVersion: 0
	},
	"queue-unavailable": {
		retryable: true,
		introducedInVersion: 0
	},
	"containment-unverified": {
		retryable: true,
		introducedInVersion: 0
	},
	"malformed-response": {
		retryable: false,
		introducedInVersion: 0
	},
	"not-configured": {
		retryable: false,
		introducedInVersion: 0
	}
};
/**
* The pure version comparison behind {@link isEmittableWorkReason}.
*
* ⛔ EXPORTED SEPARATELY ON PURPOSE, and it is not decomposition for its own sake. At version 2 no
* member has `introducedInVersion === WORK_REASONS_CONTRACT_VERSION`, so a test driving only
* {@link isEmittableWorkReason} over the real table could never observe the `false` arm — a
* ZERO-VIOLATION INVARIANT that reads as a passing guard while proving nothing. `W-F3` drives THIS
* function with synthetic version pairs so both arms are genuinely exercised on day one.
*/
function isEmittableAtVersion(introducedInVersion, contractVersion) {
	return introducedInVersion < contractVersion;
}
/**
* ⛔ THE H-forwardcompat GATE. A server may emit `reason` only when this returns `true` — i.e. the
* member shipped in a PRIOR contract version, so a client that predates the emitting server still
* recognises it.
*
* ⚠️ SEAM OBLIGATION ON WS-2/WS-3/WS-4: every server-side path that constructs a {@link WorkError}
* calls this and refuses (falling back to the nearest already-emittable member, or to
* `attempt-failed`) rather than putting an unemittable reason on the wire.
*/
function isEmittableWorkReason(reason) {
	return isEmittableAtVersion(WORK_REASON_SPECS[reason].introducedInVersion, 2);
}
/**
* Should this failure be retried?
*
* ⛔ **FAIL-CLOSED ON UNKNOWN — `null` answers `false`. Do NOT make it `null ? true`.** A reason
* this SDK version does not recognise means version skew or a new failure mode; neither is evidence
* that retrying will help, and **a permanent failure classified retryable is an unbounded
* pod-creation loop that untrusted code can trigger on demand** (R2-B5, measured).
*
* ⚠️ THE OLD DEFAULT CONFLATED TWO INPUTS, and separating them is what makes fail-closed safe.
* *"No response at all"* — a transport fault — genuinely is retryable and does NOT arrive as
* `null`: the transport raises `executor-unavailable`, which is retryable. `null` means only *"the
* server answered and named a reason I cannot read"*.
*/
function isRetryableWorkReason(reason) {
	return reason === null ? false : WORK_REASON_SPECS[reason].retryable;
}
/**
* The TOTAL map from the legacy pool vocabulary to this one, declared beside both so neither can
* grow a member with no home. ⛔ `Record<WorkPoolReason, WorkReason>` is exhaustive BY TYPE: adding
* a member to `WORK_POOL_REASONS` without adding a row here is a compile error.
*
* ⚠️ WS-7 relays legacy pool failures through this map. It is declared in WS-1 because WS-1 owns
* both unions; WS-7 imports it.
*/
const WORK_REASON_FROM_WORK_POOL_REASON = {
	"unknown-pool": "unknown-target",
	"compute-refused": "compute-refused",
	"not-authorized": "not-authorized",
	"invalid-args": "invalid-args",
	"args-too-large": "payload-too-large",
	"timeout": "timeout",
	"capacity": "capacity",
	"capacity-ceiling": "capacity-ceiling",
	"pod-start-failed": "pod-start-failed",
	"substrate-userns-unsupported": "substrate-userns-unsupported",
	"job-failed": "attempt-failed",
	"result-too-large": "result-too-large",
	"runner-unavailable": "executor-unavailable",
	"malformed-response": "malformed-response",
	"containment-unverified": "containment-unverified",
	"runner-not-configured": "not-configured"
};
/** {@link ArtifactRef} on the wire. */
const artifactRefSchema = z.object({
	runId: workRunIdSchema,
	sizeBytes: z.number().int().positive(),
	sha256: z.string().regex(/^[0-9a-f]{64}$/),
	contentType: z.string().min(1).max(128)
}).strict();
/** {@link WorkResult} on the wire. `.strict()` on both arms. */
const workResultSchema = z.discriminatedUnion("kind", [z.object({
	kind: z.literal("inline"),
	value: z.unknown().optional()
}).strict(), z.object({
	kind: z.literal("pointer"),
	ref: artifactRefSchema
}).strict()]);
/** Hard cap on any message relayed from a server, so one hostile or verbose upstream cannot produce
*  an unbounded log line or an unbounded HTTP body. Same value and same reason as
*  `MAX_RELAYED_ERROR_CHARS` in `@zion/sdk-node`'s work-pool and compute-unit clients. */
const MAX_WORK_ERROR_CHARS = 512;
/** The cap on one `log` chunk a handler forwards through `AppPoolContext.log` (R-074). 8192 chars, and
*  chars are not bytes: a UTF-16 code unit is 2 bytes in the heap and at most 3 in UTF-8, so one chunk
*  is at most 16 KiB held and 24 KiB on the wire. The progress registry buffers
*  `WORK_PROGRESS_BUFFER_CHUNKS` (64) per subscriber, so one open `runs/stream` holds at most
*  64 x 16 KiB = 1 MiB of tenant bytes in the operator's heap, against a 1 MiB per-frame dialer bound
*  that would let it hold 64 MiB. */
const MAX_WORK_LOG_CHUNK_CHARS = 8192;
/** {@link WorkError} on the wire. ⛔ `reason` is a bounded STRING, not the enum — that is the
*  forward-compat rule made STRUCTURAL: a newer server emitting a reason this client does not know
*  must still PARSE. The client narrows with {@link isWorkReason} and falls back to `null`. */
const workErrorSchema = z.object({
	reason: z.string().min(1).max(64).nullable(),
	message: z.string().min(1).max(512)
}).strict();
/**
* Narrow a parsed {@link workErrorSchema} body to a {@link WorkError}.
*
* ⛔ THE ONE COPY. The schema's `reason` is a bounded STRING and the interface's is the enum, on
* purpose — forward-compat made structural, so a newer platform emitting a reason this build does
* not know still PARSES. That gap is permanent, so every consumer needs this narrowing, and three
* were on course to hand-roll it (the operator's run store already had one, and the SDK client and
* the run client each needed the same). Three copies of one grammar is the defect; this is the fix.
*
* An unrecognised reason degrades to `null`, which is not a loss: {@link isRetryableWorkReason}'s
* `null` arm is fail-closed, and refusing to classify a reason we do not know is the correct
* treatment of it.
*/
function toWorkError(parsed) {
	return {
		reason: isWorkReason(parsed.reason) ? parsed.reason : null,
		message: parsed.message
	};
}
/**
* Narrow a parsed {@link runStatusSchema} body to a {@link RunStatus}.
*
* ⛔ EVERY OPTIONAL FIELD ALREADY LINES UP; THE ONLY GAP IS `error`, AND IT IS DELIBERATE. See
* {@link toWorkError}. Because that gap is permanent, `z.infer<typeof runStatusSchema>` is NOT
* assignable to this interface and never will be — which is exactly why this function exists rather
* than a cast.
*
* ⚠️ Spread-and-override is deliberately NOT used here. The schema is `.strict()`, so a spread would
* still be correct today, but it would silently carry any field added to the schema later straight
* into a type that does not declare it. Naming each field puts the decision about a new field at
* one place.
*
* ⛔ CORRECTED 2026-09-21: naming each field is NOT a compile error for a field that is optional on
* both sides, and this docblock claimed it was. `nodeId` and `placedAt` were added to the schema
* and to the interface, omitted here, and `tsc` stayed green at 100% coverage: an omitted optional
* is a legal object literal. `AppPools.get` then answered `undefined` for both fields over a wire
* that carried them, and an arm in `@zion/sdk-node`'s client suite is what found it. The guard for
* the next field is a producer-shaped arm that reads the field back through this function, not this
* paragraph.
*/
function toRunStatus(parsed) {
	return {
		runId: parsed.runId,
		state: parsed.state,
		attempt: parsed.attempt,
		attemptId: parsed.attemptId,
		queuePosition: parsed.queuePosition,
		startedAt: parsed.startedAt,
		finishedAt: parsed.finishedAt,
		result: parsed.result,
		error: parsed.error === void 0 ? void 0 : toWorkError(parsed.error),
		nodeId: parsed.nodeId,
		placedAt: parsed.placedAt
	};
}
/**
* Narrow a parsed {@link workEventSchema} frame to a {@link WorkEvent}.
*
* ⛔ THIS IS THE FUNCTION THE STREAM CONSUMER NEEDS, AND NOTHING NEEDED IT UNTIL NOW. Measured
* 2026-08-23: the stream door `work-stream-door.ts`, since deleted, called `workEventSchema.parse(frame)` and never assigned the
* result to {@link WorkEvent}, so the type gap had no consumer and stayed invisible. The first client
* that actually wanted the type found it immediately. A schema whose inferred type nobody has tried
* to use is the same shape as a capability with no caller.
*/
function toWorkEvent(parsed) {
	return parsed.kind === "progress" ? {
		kind: "progress",
		seq: parsed.seq,
		data: parsed.data
	} : {
		kind: "terminal",
		status: toRunStatus(parsed.status)
	};
}
/** {@link WorkHandle} on the wire. */
const workHandleSchema = z.object({ runId: workRunIdSchema }).strict();
/** The five states of one attempt. The set is closed. `queued` is reachable: D-1 gave every work target a
*  queue, and the work target left the operator in stage S3. Today an app-pool run row in state
*  `queued` or `enqueued` renders as `queued` (`toWireRunState` in the operator's
*  `app-pool-run-doors.ts`). */
const RUN_STATES = [
	"queued",
	"running",
	"succeeded",
	"failed",
	"cancelled"
];
/** Zod enum over {@link RUN_STATES}. */
const runStateSchema = z.enum(RUN_STATES);
/** The three terminal states. Exported so a consumer cannot hand-roll the set and miss `cancelled`. */
const TERMINAL_RUN_STATES = /* @__PURE__ */ new Set([
	"succeeded",
	"failed",
	"cancelled"
]);
/** True iff `state` is terminal. */
function isTerminalRunState(state) {
	return TERMINAL_RUN_STATES.has(state);
}
/** {@link RunStatus} on the wire. ⛔ The state↔field coupling above is NOT expressed as a
*  `.refine()`: the platform is the only side that can be authoritative about it, and a client-side
*  refine would REJECT a status a newer platform legitimately produced. The coupling is asserted in
*  WS-2's producer tests and in `W-S2`..`W-S5` here against literals. */
const runStatusSchema = z.object({
	runId: workRunIdSchema,
	state: runStateSchema,
	attempt: z.number().int().nonnegative(),
	attemptId: workAttemptIdSchema.optional(),
	queuePosition: z.number().int().nonnegative().optional(),
	startedAt: z.string().min(1).max(64).optional(),
	finishedAt: z.string().min(1).max(64).optional(),
	result: workResultSchema.optional(),
	error: workErrorSchema.optional(),
	nodeId: z.string().min(1).max(64).optional(),
	placedAt: z.string().datetime().optional()
}).strict();
/**
* What a `cancel()` actually did.
*
* ⛔ THREE OUTCOMES, NEVER A BOOLEAN (OVERVIEW §3.3, GOALS T5.8). `true`/`false` cannot say whether
* the work ran: "I removed it from the queue before it started" and "it had already finished" are
* both *"the run is not running"*, and a caller that must decide whether to compensate needs to
* know which. Cancel by id is genuinely absent platform-wide today — only abort-on-socket-close
* exists — so there is no legacy boolean to be compatible with.
*/
const CANCEL_OUTCOMES = [
	"not-yet-started",
	"cancelled-in-flight",
	"already-finished"
];
/** Zod enum over {@link CANCEL_OUTCOMES}. */
const cancelOutcomeSchema = z.enum(CANCEL_OUTCOMES);
/** {@link WorkEvent} on the wire. Both arms `.strict()`. */
const workEventSchema = z.discriminatedUnion("kind", [z.object({
	kind: z.literal("progress"),
	/** Monotonic from 0, so a consumer can DETECT a gap. The platform does not enforce ordering.
	*  S5 (R-074): the app-pool `runs/stream` door writes two producers into one stream. Its own
	*  `{ state }` frames count from 0 per stream; the handler's `{ type: 'progress' | 'log' }` frames
	*  carry the dispatcher's per-attempt counter. `data` tells them apart, and a gap is detectable
	*  within the `{ type }` frames only. */
	seq: z.number().int().nonnegative(),
	data: z.unknown().optional()
}).strict(), z.object({
	kind: z.literal("terminal"),
	status: runStatusSchema
}).strict()]);
/**
* The header the app-side work client carries the END USER's platform JWT on, alongside the pod's
* own projected SA token in `Authorization`.
*
* Two credentials, two principals, two fields. `Authorization` proves which app and fixture is
* calling (TokenReview, §6.1 steps 0 to 6 in the operator's `app-pool-dispatch/app-pool-authenticator.ts`).
* This header proves which org the work belongs to. They authenticate different principals and both
* are required on the same request, so neither can replace the other. On the app-pool listener one
* reader verifies it for every door, ahead of any body parse: `app-pool-listener.ts` with
* `app-pool-end-user-org-verifier.ts`.
*
* ⛔ THE VALUE IS THE BARE COMPACT JWS, WITH NO `Bearer ` PREFIX. This is not an `Authorization`
* header and it carries no auth-scheme grammar. A prefix would invite a parser that accepts the
* value both ways, and a parser that accepts two spellings of a credential is a parser that can be
* fed the wrong one. `getCurrentRequestUserToken()` (`@zion/sdk-node`) already yields the bare JWT,
* so no stripping happens anywhere.
*
* ⛔ LOWER CASE, AND THAT IS LOAD-BEARING RATHER THAN COSMETIC. Node lower-cases inbound header
* names into `IncomingMessage.headers`, so the server indexes `req.headers[this constant]` and the
* sender writes the same constant. One constant on both sides is what makes the two agree; a
* hand-written spelling on either side is a silent miss, because an absent header is a legal state
* rather than an error.
*
* Never log this value, and never put it in an error message. It is a live user credential with a
* user's identity in it. The refusal arms in `app-pool-end-user-org-verifier.ts` log a cause, never
* the token.
*
* It was declared here, in `work.ts`, because its one reader was the pool-runner listener that
* served the work doors and its one writer was the work transport. The deleted
* `remote-work-run-store.ts` stated the rule: *"the path is a wire fact and contracts owns wire
* facts."* The docblock said that a second surface would move it. Second surfaces came and the
* constant stayed here. Its readers and writers today:
* - the operator's `app-pool-listener.ts` and `app-pool-run-doors.ts`, and sdk-node's
*   `app-pools-transport.ts`;
* - core-api's `library-consent.controller.ts`, `effective-resource-grants.controller.ts` and
*   `org-members.controller.ts`, with their sdk-node and sdk-nestjs twins;
* - sdk-nestjs's `internal-route.guard.ts` and the smoke-lib fixture's `smoke-lib.controller.ts`.
*
* The 40.0.0 major (2026-10-14-app-pools-runtime-deletion, R2, R-081, R-083) removed the twelve
* work-door paths, their three request schemas, the `Work` client surface, `SubmitOptions` and
* `TargetStatus`. The pool runner that served those doors left the operator in stage S3. This header
* stays: the app-pool listener and kept core-api readers still carry it.
*/
const WORK_END_USER_TOKEN_HEADER = "x-zion-end-user-token";
/**
* The ONLY minter of {@link AuthorizedRunReader}. Returns `null` when the caller does not own the
* row — it does not throw, because the CALLER decides the refusal shape.
*
* ⛔⛔ **AND THE REFUSAL MUST BE BYTE-IDENTICAL TO A GENUINELY UNKNOWN ID.** A foreign-owner run
* answers `404 unknown-run`, never `403`. A distinguishable refusal turns this door into an
* **existence oracle**: a caller could enumerate ids and learn which ones are real. ⇒ That is also
* why {@link WORK_RUN_ID_LENGTH}'s *"identifier, never a credential"* rule is safe — the id being
* partly predictable costs nothing **only** while possession authorises nothing AND non-possession
* reveals nothing. WS-2's `T-18` pins the byte-identity for `get` and `cancel`.
*
* ⚠️ We are deliberately STRICTER than the compute-unit broker was: its `compute-unit-broker.ts`,
* deleted in stage S5, recorded `describe` as ungated and *"bounded by the UNGUESSABILITY of the handle, not authorization"*.
* ⛔ **We do not inherit that.** An unguessability argument is a statement about an attacker's
* budget, not a property of the system, and it silently stops holding the day an id appears in a
* log line, a metric label or a support ticket.
*
* ⛔⛔ WS-5 / C-026 — THE ORG IS COMPARED ONLY WHEN BOTH SIDES ASSERT ONE, AND THAT IS NOT
* TIMIDITY. C-021 refused a widening because no caller could produce a non-null org, which made
* the comparison inert; WS-5 produces one at the submit and read doors, so that reason expired. The
* OTHER half of C-021 did not: one family of callers can NEVER supply the value. A pod executing a
* work attempt holds no end-user JWT, and legacy rows admitted before this release carry
* `org_id IS NULL`. An unconditional comparison would refuse both.
*
* ⇒ **What the conditional buys, exactly:** two orgs' runs under ONE `(appKey, fixtureId)` both come
* back from `store.read`'s SELECT, because its predicate is the owner PAIR. So a caller holding a
* verified token for org B, reading a run that belongs to org A, is refused here and nowhere else.
* That is the reachable-without-compromise cross-org read round-3 security S3-7 measured.
*
* ⇒ **What it does not buy:** a caller that presents NO token is not narrowed at all. It cannot be:
* there is no third value between "this caller is org B" and "this caller named no org" that a
* comparison could act on. The narrowing is opt-in by presenting a credential, which is the only
* shape that leaves the attempt lifecycle working (C-026's hard constraint).
*
* ⚠️ Legacy rows drain rather than persist: `WORK_RUN_RETENTION_SECONDS` defaults to **86,400** with
* no override in the DP chart or its values files (C-023, measured), so the `org_id IS NULL`
* population is gone within a day of the release.
*/
function authorizeRun(caller, row) {
	if (caller.appKey !== row.appKey || caller.fixtureId !== row.fixtureId) return null;
	if (caller.orgId !== null && row.orgId !== null && caller.orgId !== row.orgId) return null;
	return caller;
}
/**
* The lifetime of one ATTEMPT LEASE, in milliseconds, DERIVED from the consumer's `ack_wait` and
* the caller's ack-extension interval.
*
* ⛔⛔ ONE DERIVATION, TWO CALLERS, AND THAT IS THE WHOLE REASON THIS IS A FUNCTION. The operator's
* config computes the value the STORE writes into `work.work_runs.lease_expires_at`; the SDK
* dispatcher computes the staleness bound at which it STOPS EXECUTING because it can no longer
* prove it holds the lease. Two constants that happen to be equal is a different property from one
* derivation shared, and only the second survives someone changing `WORK_ACK_EXTENSION_INTERVAL_MS`.
*
* ⭐ THE ARITHMETIC, so nobody has to re-derive it. Let `W = ack_wait` and `R` = the renewal
* interval. Both the ack extension and the lease renewal ride the SAME timer tick, so their
* last-success instants coincide at some `t`; a redelivery therefore arrives no earlier than
* `t + W`, and the lease expires at `t + L`.
*   - CEILING. For the FIRST redelivery after a dispatcher death to find an EXPIRED lease we need
*     `L < W`. The binding case is the tick on which `extendAck()` failed while the renewal
*     succeeded — the last renewal is then one interval later than the last extension — which
*     gives `R + L < W`, i.e. `L <= W - R`. Exceeding it costs one of the five deliveries.
*   - FLOOR. A renewal must land before the lease it extends lapses, so `L >= R`. This floor is
*     SOFT: `renewLease` is fenced on `attempt_id`, never on lease liveness, so the holder can
*     re-extend an already-expired lease, and an expired lease is only consequential when a
*     redelivery arrives — which requires the ack extension to have stopped too.
* ⇒ `L = W - R`, the ceiling exactly, which at the default `R = 20 000` is also `2R` — two full
* renewal intervals of headroom over the floor.
*
* ⛔ THE CALLER MUST HAVE ALREADY REFUSED AN INCOHERENT INTERVAL. This function does not clamp: a
* clamp would silently substitute a value the operator did not choose, so the data plane would work
* while its configuration was a lie (the rule that the `POOL_MAX_ARTIFACT_BYTES` docblock in the
* operator's `config.ts` states for the artifact ceiling). The operator refuses at boot instead — see `config.ts`'s inline refusal on
* `WORK_ACK_EXTENSION_INTERVAL_MS`.
*
* @param ackExtensionIntervalMs how often the holder calls `extendAck()` + `renewLease()`.
* @returns `WORKER_CONSUMER_CONFIG.ack_wait_s * 1000 - ackExtensionIntervalMs`. May be <= 0 for an
*   incoherent input — the caller refuses, this function does not lie about it.
*/
function workAttemptLeaseMs(ackExtensionIntervalMs) {
	return WORKER_CONSUMER_CONFIG.ack_wait_s * 1e3 - ackExtensionIntervalMs;
}
//#endregion
export { CANCEL_OUTCOMES, EXECUTION_SHAPES, MAX_WORK_ERROR_CHARS, MAX_WORK_LOG_CHUNK_CHARS, MAX_WORK_TIMEOUT_MS, RUN_STATES, TERMINAL_RUN_STATES, WORK_ATTEMPT_ID_HEX_LEN, WORK_ATTEMPT_ID_PATTERN, WORK_END_USER_TOKEN_HEADER, WORK_IDEMPOTENCY_KEY_MAX_LENGTH, WORK_IDEMPOTENCY_KEY_PATTERN, WORK_REASONS, WORK_REASONS_CONTRACT_VERSION, WORK_REASON_FROM_WORK_POOL_REASON, WORK_REASON_SPECS, WORK_RUN_ID_LENGTH, WORK_RUN_ID_PATTERN, artifactRefSchema, authorizeRun, cancelOutcomeSchema, executionShapeSchema, isEmittableAtVersion, isEmittableWorkReason, isExecutionShape, isRetryableWorkReason, isTerminalRunState, isWorkReason, runStateSchema, runStatusSchema, toRunStatus, toWorkError, toWorkEvent, workAttemptIdSchema, workAttemptLeaseMs, workErrorSchema, workEventSchema, workHandleSchema, workIdempotencyKeySchema, workResultSchema, workRunIdSchema };

//# sourceMappingURL=work.js.map